import * as THREE from 'three'
import {
  type GLTF,
  GLTFLoader,
  type GLTFParser as ThreeGltfParser,
} from 'three/addons/loaders/GLTFLoader.js'
import {
  GltfMemoryBudgetExceededError,
  MeshAssetLoadError,
  MeshLoadCancelledError,
  SkinnedMeshNotSupportedError,
} from '@/plugins/mesh/errors'
import { createMeshLoadResult } from '@/plugins/mesh/loaders/createMeshLoadResult'
import {
  attachGltfExtensionResults,
  disposeGltfExtensionResults,
  hasHandledExtensionPrimitives,
  processThreeGltfExtensions,
} from '@/plugins/mesh/loaders/GltfExtensionIntegration'
import { BrowserGltfImageDecoder } from '@/plugins/mesh/loaders/gltfLowMemory/DefaultGltfCoreMaterialFactory'
import { estimateThreeTextureBytes } from '@/plugins/mesh/loaders/gltfLowMemory/estimateTextureBytes'
import { startGltfRuntimePerformanceMonitor } from '@/plugins/mesh/loaders/gltfLowMemory/gltfRuntimePerformance'
import { getConcreteGltfTextureMaxSize } from '@/plugins/mesh/loaders/gltfLowMemory/gltfTextureAutoSize'
import { ImageBitmapLifetime } from '@/plugins/mesh/loaders/gltfLowMemory/imageBitmapLifetime'
import {
  LowMemoryGltfLoader,
  type LowMemoryGltfLoaderDependencies,
} from '@/plugins/mesh/loaders/gltfLowMemory/LowMemoryGltfLoader'
import { resolveGltfLoadingOptions } from '@/plugins/mesh/loaders/gltfLowMemory/lowMemoryOptions'
import { installMeshKtx2 } from '@/plugins/mesh/loaders/installMeshKtx2'
import {
  createMeshProgressReporter,
  describeMeshSource,
  getMeshSourceExtension,
  loadMeshSourceData,
  type MeshSourceReadPerformanceReport,
  resolveMeshBaseUrl,
} from '@/plugins/mesh/loaders/loaderUtils'
import { borrowMeshKtx2Loader } from '@/plugins/mesh/loaders/MeshKtx2Service'
import { MeshTextureTaskPool } from '@/plugins/mesh/loaders/MeshTextureTaskPool'
import type { MeshLoadContext } from '@/plugins/mesh/loaders/meshLoadContext'
import { ProgressiveGltfSession } from '@/plugins/mesh/loaders/ProgressiveGltfSession'
import { MeshPrimitiveNormalizer } from '@/plugins/mesh/MeshPrimitiveNormalizer'
import { enumerateMaterialTextures, MeshResourceTracker } from '@/plugins/mesh/MeshResourceTracker'
import type {
  GltfLoadingOptionsInput,
  MeshKtx2Loader,
  MeshKtx2Report,
  MeshLoadRequest,
  MeshLoadResult,
  MeshOptimizationOptionsInput,
} from '@/plugins/mesh/types'
import type { GltfExtensionRegistry } from '@/runtime/GltfExtensionRegistry'
import type { IDracoDecodeService } from '@/shared/draco/DracoDecodeService'
import { DracoBudgetExceededError } from '@/shared/draco/DracoDecodeService'
import { installDracoGltfBridge } from '@/shared/draco/gltfBridge'
import { getLoggerManager, LogLevel } from '@/shared/logging'
import type { AssetLoader, LoadContext } from '@/shared/types/assets'

const log = getLoggerManager().getLogger('mesh:gltf:standard')
const sourceLog = getLoggerManager().getLogger('mesh:gltf:source')
const textureLog = getLoggerManager().getLogger('mesh:gltf:texture')
const runtimeLog = getLoggerManager().getLogger('mesh:gltf:runtime')

function getImageDimensions(image: TexImageSource): readonly [number, number] {
  const sized = image as TexImageSource & {
    readonly naturalWidth?: number
    readonly naturalHeight?: number
    readonly width?: number
    readonly height?: number
  }
  return [sized.naturalWidth ?? sized.width ?? 0, sized.naturalHeight ?? sized.height ?? 0]
}

/** 可替换的 glTF 解析器最小契约。 */
export interface GltfParser {
  /** 内置解析器用于解码后预算复核；自定义解析器无需实现。 */
  usedDraco?(): boolean
  /** 默认解析器的 KTX2 实际输出统计。 */
  getKtx2Report?(): MeshKtx2Report | undefined
  /** 判断失败前已清理的纹理，避免迟到场景重复销毁。 */
  wasTextureDisposed?(texture: THREE.Texture): boolean
  /** 可选 Three.js 扩展注册入口；未提供时保持完整结果交付。 */
  register?: GLTFLoader['register']
  parse(
    data: ArrayBuffer | string,
    path: string,
    onLoad: (gltf: GLTF) => void,
    onError?: (error: ErrorEvent) => void
  ): void
  /** 解析失败或取消时释放尚未转移给 Texture 的图片。 */
  disposeImages?(): void
}

/** glTF/GLB Loader 配置。 */
export interface GltfMeshLoaderOptions {
  /** 显式借用共享 Draco 服务；Loader 不负责销毁。 */
  draco?: IDracoDecodeService
  /** 默认解析器使用的 URL 映射和加载计数管理器。 */
  loadingManager?: THREE.LoadingManager
  /** 宿主拥有的已初始化 KTX2 实例与 renderer 尺寸限制。 */
  ktx2?: MeshKtx2Loader
  /** 主要用于接入自定义 Three.js GLTFLoader 配置或测试解析内核。 */
  createParser?: (request: MeshLoadRequest) => GltfParser
  /** Loader 级 glTF 三态加载默认配置。 */
  loading?: GltfLoadingOptionsInput
  /** 低内存计划使用的默认优化配置。 */
  optimization?: MeshOptimizationOptionsInput
  /** 可替换的低内存管线组件。 */
  lowMemory?: LowMemoryGltfLoaderDependencies
  /** 可注入的引擎实例级 glTF extension registry。 */
  extensionRegistry?: GltfExtensionRegistry
}

function toRequestHeader(headers: HeadersInit | undefined): Record<string, string> {
  if (!headers) return {}
  return Object.fromEntries(new Headers(headers).entries())
}

function percentile(values: readonly number[], ratio: number): number {
  if (values.length === 0) return 0
  const sorted = [...values].sort((left, right) => left - right)
  return sorted[Math.min(sorted.length - 1, Math.floor((sorted.length - 1) * ratio))] ?? 0
}

type StandardImageSourceKind = 'blob' | 'data' | 'http' | 'other-url'

function getStandardImageSourceKind(url: string): StandardImageSourceKind {
  try {
    const protocol = new URL(url, globalThis.location?.href).protocol
    if (protocol === 'blob:') return 'blob'
    if (protocol === 'data:') return 'data'
    if (protocol === 'http:' || protocol === 'https:') return 'http'
  } catch {
    // 非标准 URL 统一计入其他来源。
  }
  return 'other-url'
}

interface StandardImageTask {
  readonly resolvedUrl: string
  readonly queuedAt: number
  readonly onLoad: (data: TexImageSource) => void
  readonly onError?: (error: unknown) => void
  settled: boolean
}

class ResizingGltfImageLoader extends THREE.Loader<TexImageSource> {
  readonly isImageBitmapLoader = true

  private readonly decoder: BrowserGltfImageDecoder
  private readonly diagnosticsEnabled: boolean
  private readonly decodedReservationBytes: number
  private readonly taskController = new AbortController()
  private readonly pendingTasks: StandardImageTask[] = []
  private readonly decodedImages = new Set<TexImageSource>()
  private readonly imageLifetime = new ImageBitmapLifetime()
  private readonly queueWaitSamples: number[] = []
  private readonly responseWaitSamples: number[] = []
  private readonly bodyReadSamples: number[] = []
  private readonly decodeSamples: number[] = []
  private readonly sourceCounts: Record<StandardImageSourceKind, number> = {
    blob: 0,
    data: 0,
    http: 0,
    'other-url': 0,
  }
  private activeTasks = 0
  private activeFetches = 0
  private activeDecodes = 0
  private activeDecodeBytes = 0
  private peakTaskConcurrency = 0
  private peakFetchConcurrency = 0
  private peakDecodeConcurrency = 0
  private peakDecodeBytes = 0
  private peakQueueDepth = 0
  private scheduledImageCount = 0
  private imageCount = 0
  private encodedImageBytes = 0
  private queueWaitMs = 0
  private responseWaitMs = 0
  private bodyReadMs = 0
  private imageDecodeMs = 0
  private activeTaskMs = 0
  private firstQueuedAt = 0
  private lastCompletedAt = 0
  private slowestDecodeMs = 0
  private slowestImageBytes = 0
  private slowestImageWidth = 0
  private slowestImageHeight = 0
  private performanceLogged = false
  private disposed = false

  constructor(
    manager: THREE.LoadingManager,
    maxSize: number,
    private readonly maxConcurrentTasks: number,
    private readonly request: MeshLoadRequest,
    private readonly textureTaskPool?: MeshTextureTaskPool,
    private readonly onTextureError?: (error: unknown) => void
  ) {
    super(manager)
    this.decoder = new BrowserGltfImageDecoder(maxSize)
    this.decodedReservationBytes = Number.isFinite(maxSize) ? maxSize * maxSize * 4 : 0
    this.diagnosticsEnabled = textureLog.isEnabled(LogLevel.DEBUG)
    if (request.signal?.aborted) {
      this.taskController.abort(request.signal.reason)
    } else {
      request.signal?.addEventListener('abort', this.handleRequestAbort, { once: true })
    }
  }

  load(
    url: string,
    onLoad: (data: TexImageSource) => void,
    _onProgress?: (event: ProgressEvent) => void,
    onError?: (error: unknown) => void
  ): void {
    const resolvedUrl = this.manager.resolveURL(this.path ? `${this.path}${url}` : url)
    const queuedAt = getLoggerManager().now()
    const task: StandardImageTask = {
      resolvedUrl,
      queuedAt,
      onLoad,
      onError,
      settled: false,
    }
    this.manager.itemStart(resolvedUrl)
    this.scheduledImageCount += 1
    if (this.scheduledImageCount === 1) this.firstQueuedAt = queuedAt
    this.sourceCounts[getStandardImageSourceKind(resolvedUrl)] += 1
    if (this.disposed || this.taskController.signal.aborted) {
      this.failTask(task, this.createCancellationError())
      return
    }
    this.pendingTasks.push(task)
    this.peakQueueDepth = Math.max(this.peakQueueDepth, this.pendingTasks.length)
    this.drainQueue()
  }

  bindDisposal(gltf: GLTF): void {
    this.detachRequestAbort()
    const boundImages = new Set<TexImageSource>()
    const textures = new Set<THREE.Texture>()
    for (const scene of gltf.scenes) {
      scene.traverse((object) => {
        const material = (object as THREE.Mesh).material
        const materials = Array.isArray(material) ? material : material ? [material] : []
        for (const item of materials) {
          for (const texture of enumerateMaterialTextures(item)) textures.add(texture)
        }
      })
    }
    this.logPerformanceSummary(textures)
    for (const texture of textures) {
      const image = texture.image as TexImageSource & { close?: () => void }
      if (typeof image?.close !== 'function') continue
      boundImages.add(image)
      this.imageLifetime.bind(texture, image)
    }
    for (const image of this.decodedImages) {
      if (!boundImages.has(image)) this.imageLifetime.closeIfUnreferenced(image)
    }
    this.decodedImages.clear()
  }

  disposeImages(): void {
    if (this.disposed) return
    this.disposed = true
    this.detachRequestAbort()
    if (!this.taskController.signal.aborted) {
      this.taskController.abort('图片 Loader 已释放')
    }
    this.failPendingTasks(this.createCancellationError())
    this.imageLifetime.dispose()
    this.decodedImages.clear()
  }

  private readonly handleRequestAbort = () => {
    if (!this.taskController.signal.aborted) {
      this.taskController.abort(this.request.signal?.reason)
    }
    this.failPendingTasks(this.createCancellationError())
  }

  private detachRequestAbort(): void {
    this.request.signal?.removeEventListener('abort', this.handleRequestAbort)
  }

  private createCancellationError(): MeshLoadCancelledError {
    return new MeshLoadCancelledError('(glTF 图片)', {
      cause: this.taskController.signal.reason ?? '图片 Loader 已释放',
    })
  }

  private drainQueue(): void {
    while (
      !this.disposed &&
      !this.taskController.signal.aborted &&
      this.activeTasks < this.maxConcurrentTasks &&
      this.pendingTasks.length > 0
    ) {
      const task = this.pendingTasks.shift()
      if (!task) return
      this.activeTasks += 1
      this.peakTaskConcurrency = Math.max(this.peakTaskConcurrency, this.activeTasks)
      const queueWaitMs = getLoggerManager().now() - task.queuedAt
      this.queueWaitMs += queueWaitMs
      if (this.diagnosticsEnabled) this.queueWaitSamples.push(queueWaitMs)
      void this.executeTask(task).finally(() => {
        this.activeTasks -= 1
        this.drainQueue()
      })
    }
  }

  private async executeTask(task: StandardImageTask): Promise<void> {
    const taskStartedAt = getLoggerManager().now()
    let release: (() => void) | undefined
    try {
      release = await this.textureTaskPool?.acquire(this.taskController.signal)
      if (this.disposed || this.taskController.signal.aborted) throw this.createCancellationError()
      const blob = await this.fetchImageBlob(task.resolvedUrl)
      const decodeStartedAt = getLoggerManager().now()
      const activeBytes = blob.size + this.decodedReservationBytes
      this.activeDecodes += 1
      this.activeDecodeBytes += activeBytes
      this.peakDecodeConcurrency = Math.max(this.peakDecodeConcurrency, this.activeDecodes)
      this.peakDecodeBytes = Math.max(this.peakDecodeBytes, this.activeDecodeBytes)
      let image: TexImageSource
      try {
        image = await this.decoder.decode(blob, this.taskController.signal)
      } finally {
        this.activeDecodes -= 1
        this.activeDecodeBytes -= activeBytes
      }
      const decodeMs = getLoggerManager().now() - decodeStartedAt
      this.imageDecodeMs += decodeMs
      if (this.diagnosticsEnabled) this.decodeSamples.push(decodeMs)
      this.imageCount += 1
      this.encodedImageBytes += blob.size
      if (this.slowestImageBytes === 0 || decodeMs > this.slowestDecodeMs) {
        const [width, height] = getImageDimensions(image)
        this.slowestDecodeMs = decodeMs
        this.slowestImageBytes = blob.size
        this.slowestImageWidth = width
        this.slowestImageHeight = height
      }
      if (this.disposed || this.taskController.signal.aborted) {
        this.imageLifetime.closeIfUnreferenced(image)
        throw this.createCancellationError()
      }
      this.imageLifetime.track(image)
      this.decodedImages.add(image)
      task.onLoad(image)
      this.completeTask(task)
    } catch (error) {
      this.failTask(task, error)
    } finally {
      release?.()
      const completedAt = getLoggerManager().now()
      this.activeTaskMs += completedAt - taskStartedAt
      this.lastCompletedAt = completedAt
    }
  }

  private async fetchImageBlob(resolvedUrl: string): Promise<Blob> {
    this.activeFetches += 1
    this.peakFetchConcurrency = Math.max(this.peakFetchConcurrency, this.activeFetches)
    try {
      const responseStartedAt = getLoggerManager().now()
      let response: Response
      try {
        response = await fetch(resolvedUrl, {
          credentials: this.request.credentials ?? 'same-origin',
          headers: this.request.headers,
          signal: this.taskController.signal,
        })
      } finally {
        const responseWaitMs = getLoggerManager().now() - responseStartedAt
        this.responseWaitMs += responseWaitMs
        if (this.diagnosticsEnabled) this.responseWaitSamples.push(responseWaitMs)
      }
      if (!response.ok) {
        throw new Error(`glTF 图片请求失败: ${response.status} ${response.statusText}`)
      }
      const bodyStartedAt = getLoggerManager().now()
      let blob: Blob
      try {
        blob = await response.blob()
      } finally {
        const bodyReadMs = getLoggerManager().now() - bodyStartedAt
        this.bodyReadMs += bodyReadMs
        if (this.diagnosticsEnabled) this.bodyReadSamples.push(bodyReadMs)
      }
      return blob
    } finally {
      this.activeFetches -= 1
    }
  }

  private completeTask(task: StandardImageTask): void {
    if (task.settled) return
    task.settled = true
    this.manager.itemEnd(task.resolvedUrl)
  }

  private failTask(task: StandardImageTask, error: unknown): void {
    if (task.settled) return
    task.settled = true
    const cancelled = this.taskController.signal.aborted || error instanceof MeshLoadCancelledError
    this.onTextureError?.(error)
    try {
      task.onError?.(error)
    } finally {
      try {
        if (!cancelled) this.manager.itemError(task.resolvedUrl)
      } finally {
        this.manager.itemEnd(task.resolvedUrl)
      }
    }
  }

  private failPendingTasks(error: unknown): void {
    for (const task of this.pendingTasks.splice(0)) this.failTask(task, error)
  }

  private logPerformanceSummary(textures: ReadonlySet<THREE.Texture>): void {
    if (this.performanceLogged || this.imageCount === 0 || !this.diagnosticsEnabled) return
    this.performanceLogged = true
    const decoding = this.decoder.getPerformanceReport()
    const textureWallMs = Math.max(0, this.lastCompletedAt - this.firstQueuedAt)
    const effectiveConcurrency = Math.min(this.maxConcurrentTasks, this.scheduledImageCount)
    const workerUtilizationPct =
      textureWallMs > 0 && effectiveConcurrency > 0
        ? Math.min(100, (this.activeTaskMs / (textureWallMs * effectiveConcurrency)) * 100)
        : 0
    const estimatedGpuBytes = [...textures].reduce(
      (total, texture) => total + estimateThreeTextureBytes(texture),
      0
    )
    textureLog.debug(
      `event=mesh.gltf.texture.summary source=${describeMeshSource(this.request)} path=standard images=${
        this.imageCount
      } scheduledImages=${this.scheduledImageCount} decoder=${decoding.decoderKind} encodedBytes=${
        this.encodedImageBytes
      } queueWaitMs=${this.queueWaitMs.toFixed(1)} queueWaitP95Ms=${percentile(
        this.queueWaitSamples,
        0.95
      ).toFixed(1)} queueWaitMaxMs=${Math.max(0, ...this.queueWaitSamples).toFixed(
        1
      )} responseWaitMs=${this.responseWaitMs.toFixed(1)} responseWaitP95Ms=${percentile(
        this.responseWaitSamples,
        0.95
      ).toFixed(1)} responseWaitMaxMs=${Math.max(0, ...this.responseWaitSamples).toFixed(
        1
      )} bodyReadMs=${this.bodyReadMs.toFixed(1)} bodyReadP95Ms=${percentile(
        this.bodyReadSamples,
        0.95
      ).toFixed(1)} bodyReadMaxMs=${Math.max(0, ...this.bodyReadSamples).toFixed(1)} fetchMs=${(
        this.responseWaitMs + this.bodyReadMs
      ).toFixed(1)} decodeMs=${this.imageDecodeMs.toFixed(1)} decodeP50Ms=${percentile(
        this.decodeSamples,
        0.5
      ).toFixed(1)} decodeP95Ms=${percentile(this.decodeSamples, 0.95).toFixed(
        1
      )} decodeMaxMs=${Math.max(0, ...this.decodeSamples).toFixed(
        1
      )} textureWallMs=${textureWallMs.toFixed(1)} headerMs=${decoding.headerMs.toFixed(
        1
      )} directDecodeMs=${decoding.directDecodeMs.toFixed(1)} fullDecodeMs=${decoding.fullDecodeMs.toFixed(
        1
      )} resizeMs=${decoding.resizeMs.toFixed(1)} directResize=${
        decoding.directResizeCount
      } fallbackResize=${decoding.fallbackResizeCount} unchanged=${
        decoding.unchangedCount
      } slowestDecodeMs=${this.slowestDecodeMs.toFixed(1)} slowestBytes=${
        this.slowestImageBytes
      } slowestOutput=${this.slowestImageWidth}x${
        this.slowestImageHeight
      } configuredConcurrency=${this.maxConcurrentTasks} effectiveConcurrency=${
        effectiveConcurrency
      } peakPipelineConcurrency=${this.peakTaskConcurrency} peakFetchConcurrency=${
        this.peakFetchConcurrency
      } peakDecodeConcurrency=${this.peakDecodeConcurrency} peakQueueDepth=${
        this.peakQueueDepth
      } averageActiveWorkers=${
        textureWallMs > 0 ? (this.activeTaskMs / textureWallMs).toFixed(2) : '0.00'
      } workerUtilizationPct=${workerUtilizationPct.toFixed(1)} blobImages=${
        this.sourceCounts.blob
      } dataUriImages=${this.sourceCounts.data} httpImages=${
        this.sourceCounts.http
      } otherUrlImages=${this.sourceCounts['other-url']} peakDecodeBytes=${
        this.peakDecodeBytes
      } estimatedGpuBytes=${estimatedGpuBytes} estimatedPeakWorkingSetBytes=${
        estimatedGpuBytes + this.peakDecodeBytes
      } timingKind=cumulative-tasks`
    )
  }
}

function installResizingImageLoader(
  loader: GLTFLoader,
  request: MeshLoadRequest,
  maxSize: number,
  maxConcurrentTasks: number,
  textureTaskPool?: MeshTextureTaskPool,
  onTextureError?: (error: unknown) => void
): () => void {
  let imageLoader: ResizingGltfImageLoader | undefined
  loader.register((parser: ThreeGltfParser) => {
    imageLoader = new ResizingGltfImageLoader(
      parser.options.manager,
      maxSize,
      maxConcurrentTasks,
      request,
      textureTaskPool,
      onTextureError
    )
    return {
      name: 'SHARE3D_texture_max_size',
      beforeRoot: () => {
        parser.textureLoader = imageLoader as unknown as THREE.ImageBitmapLoader
        return null
      },
      afterRoot: (gltf) => {
        imageLoader?.bindDisposal(gltf)
        return null
      },
    }
  })
  return () => imageLoader?.disposeImages()
}

function createDefaultParser(request: MeshLoadRequest, context?: MeshLoadContext): GLTFLoader {
  const loader = new GLTFLoader(context?.loadingManager)
  loader.setRequestHeader(toRequestHeader(request.headers))
  loader.setWithCredentials(request.credentials === 'include')
  const loading = resolveGltfLoadingOptions(undefined, request.gltf)
  const pool =
    context?.textureTaskPool ?? new MeshTextureTaskPool(loading.texture.maxConcurrentDecodes)
  const disposeImages = installResizingImageLoader(
    loader,
    request,
    getConcreteGltfTextureMaxSize(loading),
    loading.texture.maxConcurrentDecodes,
    pool,
    context?.onTextureError
  )
  const ktx2 = installMeshKtx2(loader, request, pool, context?.ktx2Service, (error) =>
    context?.onTextureError?.(error)
  )
  const draco = context?.dracoService
    ? installDracoGltfBridge(loader, context.dracoService, {
        signal: context.signal ?? request.signal,
        source: describeMeshSource(request),
        maxKnownBytes: loading.budget.maxTransientBytes,
        maxGeometryBytes: loading.budget.maxFinalGeometryBytes,
      })
    : undefined
  return Object.assign(loader, {
    usedDraco: draco?.usedDraco,
    disposeImages: () => {
      disposeImages()
      ktx2.dispose()
    },
    wasTextureDisposed: ktx2.wasDisposed,
    getKtx2Report: ktx2.getReport,
  })
}

function disposeLateGltfResult(gltf: GLTF, parser?: GltfParser): void {
  const resources = new MeshResourceTracker()
  try {
    resources.trackObject3D(gltf.scene)
    for (const texture of resources.textures)
      if (parser?.wasTextureDisposed?.(texture)) resources.textures.delete(texture)
    resources.dispose()
  } catch {
    // 取消后的迟到结果没有调用方，跟踪器仍会尽量释放其余资源。
  }
}

function parseGltf(
  parser: GltfParser,
  data: ArrayBuffer | string,
  path: string,
  signal: AbortSignal | undefined,
  source: string,
  disposeLate: (gltf: GLTF) => void = disposeLateGltfResult
): Promise<GLTF> {
  return new Promise((resolve, reject) => {
    let settled = false
    const cleanup = () => signal?.removeEventListener('abort', handleAbort)
    const handleAbort = () => {
      if (settled) return
      settled = true
      cleanup()
      reject(new MeshLoadCancelledError(source, { cause: signal?.reason }))
    }

    if (signal?.aborted) {
      handleAbort()
      return
    }
    signal?.addEventListener('abort', handleAbort, { once: true })

    try {
      parser.parse(
        data,
        path,
        (gltf) => {
          if (settled) {
            if (signal?.aborted) disposeLate(gltf)
            return
          }
          settled = true
          cleanup()
          resolve(gltf)
        },
        (error) => {
          if (settled) return
          settled = true
          cleanup()
          reject(error)
        }
      )
    } catch (error) {
      if (settled) return
      settled = true
      cleanup()
      reject(error)
    }
  })
}

function findSkinnedMesh(root: THREE.Object3D): THREE.SkinnedMesh | undefined {
  let found: THREE.SkinnedMesh | undefined
  root.traverse((object) => {
    if (!found && object instanceof THREE.SkinnedMesh) {
      found = object
    }
  })
  return found
}

/** glTF 与 GLB 共用的静态网格加载内核。 */
class StandardGltfMeshLoader implements AssetLoader<MeshLoadRequest, MeshLoadResult> {
  readonly format: 'gltf' | 'glb'
  readonly detectPriority = 10

  private readonly createParser: (request: MeshLoadRequest, context?: MeshLoadContext) => GltfParser
  private readonly normalizer = new MeshPrimitiveNormalizer()
  private readonly extensionRegistry?: GltfExtensionRegistry

  constructor(options: GltfMeshLoaderOptions = {}, format: 'gltf' | 'glb' = 'gltf') {
    this.format = format
    this.createParser = options.createParser ?? createDefaultParser
    this.extensionRegistry = options.extensionRegistry
    if (
      options.ktx2 &&
      (!Number.isFinite(options.ktx2.maxTextureSize) || options.ktx2.maxTextureSize < 1)
    )
      throw new RangeError('KTX2 maxTextureSize 必须来自 renderer 的正有限尺寸')
  }

  canLoad(request: MeshLoadRequest): boolean {
    if (request.format) return request.format.toLowerCase() === this.format
    return getMeshSourceExtension(request.url) === this.format
  }

  async load(request: MeshLoadRequest, context: MeshLoadContext): Promise<MeshLoadResult> {
    const source = describeMeshSource(request)
    const signal = context.signal ?? request.signal
    const reportProgress = createMeshProgressReporter(request, context)
    const startedAt = getLoggerManager().now()
    const resources = new MeshResourceTracker()
    const textureBudget = resolveGltfLoadingOptions(undefined, request.gltf).budget.maxTextureBytes
    const seenTextures = new Set<THREE.Texture>()
    const seenGeometryArrays = new Set<ArrayBufferView>()
    let textureBytes = 0
    let geometryBytes = 0
    const checkTextures = (textures: Iterable<THREE.Texture>) => {
      for (const texture of textures) {
        if (seenTextures.has(texture)) continue
        seenTextures.add(texture)
        textureBytes += estimateThreeTextureBytes(texture)
      }
      if (textureBytes > textureBudget) {
        throw new GltfMemoryBudgetExceededError(
          source,
          'budget-texture-exceeded',
          textureBytes,
          textureBudget
        )
      }
    }
    const checkDracoGeometry = (geometries: Iterable<THREE.BufferGeometry>) => {
      if (!parser?.usedDraco?.()) return
      for (const geometry of geometries) {
        for (const attribute of [...Object.values(geometry.attributes), geometry.index]) {
          if (!attribute) continue
          const array = 'data' in attribute ? attribute.data.array : attribute.array
          if (seenGeometryArrays.has(array)) continue
          seenGeometryArrays.add(array)
          geometryBytes += array.byteLength
        }
      }
      const limit = resolveGltfLoadingOptions(undefined, request.gltf).budget.maxFinalGeometryBytes
      if (geometryBytes > limit)
        throw new GltfMemoryBudgetExceededError(
          source,
          'budget-final-exceeded',
          geometryBytes,
          limit
        )
    }
    const progressive = context.onPart
      ? new ProgressiveGltfSession(
          resources,
          (part) => {
            checkDracoGeometry(part.resources.geometries)
            checkTextures(part.resources.textures)
            context.onPart!(part)
          },
          this.format,
          source,
          signal
        )
      : undefined
    let parser: GltfParser | undefined
    let extensionResults:
      | ReadonlyMap<string, import('@/runtime/GltfExtensionRegistry').GltfExtensionResult>
      | undefined
    let parsingFailed = false
    let sourceReadMs = 0
    let parseMs = 0
    let normalizeMs = 0
    let sourcePerformance: MeshSourceReadPerformanceReport | undefined
    let runtimeMonitor: ReturnType<typeof startGltfRuntimePerformanceMonitor> | undefined
    let runtimeFinished = false
    const finishRuntimeMonitoring = (status: 'complete' | 'failed') => {
      if (runtimeFinished) return
      runtimeFinished = true
      const runtime = runtimeMonitor?.stop()
      if (!runtime) return
      runtimeLog.debug(
        `event=mesh.gltf.runtime.summary source=${source} path=standard status=${status} wallMs=${runtime.wallMs.toFixed(
          1
        )} longTaskSupported=${runtime.longTaskSupported} longTasks=${runtime.longTaskCount} longTaskMs=${runtime.longTaskMs.toFixed(
          1
        )} longTaskMaxMs=${runtime.longTaskMaxMs.toFixed(
          1
        )} eventLoopSamples=${runtime.eventLoopSampleCount} eventLoopLagMs=${runtime.eventLoopLagMs.toFixed(
          1
        )} eventLoopLagP95Ms=${runtime.eventLoopLagP95Ms.toFixed(
          1
        )} eventLoopLagMaxMs=${runtime.eventLoopLagMaxMs.toFixed(1)} timingKind=wall-clock`
      )
    }

    try {
      reportProgress(0)
      const sourceStartedAt = getLoggerManager().now()
      const loading = resolveGltfLoadingOptions(undefined, request.gltf)
      const data = await loadMeshSourceData(
        request,
        context,
        (progress) => reportProgress(progress * 0.55),
        sourceLog.isEnabled(LogLevel.DEBUG)
          ? (performanceReport) => {
              sourcePerformance = performanceReport
            }
          : undefined,
        { maxPreallocateBytes: loading.budget.maxTransientBytes }
      )
      sourceReadMs = getLoggerManager().now() - sourceStartedAt
      if (sourcePerformance) {
        sourceLog.debug(
          `event=mesh.gltf.source.summary source=${source} path=standard sourceKind=${sourcePerformance.sourceKind} strategy=${sourcePerformance.strategy} bytes=${sourcePerformance.byteLength} responseWaitMs=${sourcePerformance.responseWaitMs.toFixed(
            1
          )} bodyReadMs=${sourcePerformance.bodyReadMs.toFixed(
            1
          )} bodyCopyMs=${sourcePerformance.bodyCopyMs.toFixed(
            1
          )} chunks=${sourcePerformance.chunkCount} estimatedPeakBufferedBytes=${
            sourcePerformance.estimatedPeakBufferedBytes
          } sourceReadMs=${sourceReadMs.toFixed(1)} timingKind=wall-clock`
        )
      }
      if (signal?.aborted) throw new MeshLoadCancelledError(source)

      const parseStartedAt = getLoggerManager().now()
      runtimeMonitor = startGltfRuntimePerformanceMonitor(runtimeLog.isEnabled(LogLevel.DEBUG))
      let rejectTextureError!: (error: unknown) => void
      const textureFailure = new Promise<never>((_resolve, reject) => {
        rejectTextureError = reject
      })
      parser = this.createParser(
        { ...request, signal },
        {
          ...context,
          onTextureError: (error) => {
            progressive?.close()
            rejectTextureError(error)
          },
        }
      )
      // 自定义解析器可能在节点钩子中改变变换或 Mesh 数量，沿用处理后的浅节点。
      progressive?.install(parser, this.createParser === createDefaultParser)
      const parsing = parseGltf(
        parser,
        data,
        resolveMeshBaseUrl(request),
        signal,
        source,
        progressive
          ? (late) => progressive.disposeLate(late.scene)
          : (late) => disposeLateGltfResult(late, parser)
      ).then((gltf) => {
        if (parsingFailed) {
          if (progressive) progressive.disposeLate(gltf.scene)
          else disposeLateGltfResult(gltf, parser)
        }
        return gltf
      })
      const gltf = await Promise.race([parsing, textureFailure])
      parseMs = getLoggerManager().now() - parseStartedAt
      finishRuntimeMonitoring('complete')
      resources.trackObject3D(gltf.scene)
      if (this.extensionRegistry) {
        extensionResults = await processThreeGltfExtensions(this.extensionRegistry, gltf, signal)
      }
      checkDracoGeometry(resources.geometries)
      checkTextures(resources.textures)
      reportProgress(0.78)

      const skinnedMesh = findSkinnedMesh(gltf.scene)
      if (skinnedMesh) {
        throw new SkinnedMeshNotSupportedError(source, skinnedMesh.name || undefined)
      }

      const normalizeStartedAt = getLoggerManager().now()
      const normalized = this.normalizer.normalize(gltf.scene, resources)
      checkDracoGeometry(resources.geometries)
      normalizeMs = getLoggerManager().now() - normalizeStartedAt
      if (
        normalized.primitives.length === 0 &&
        !hasHandledExtensionPrimitives(extensionResults ?? new Map())
      ) {
        throw new Error(`${this.format} 未包含可渲染静态 Mesh`)
      }
      reportProgress(0.95)
      if (signal?.aborted) throw new MeshLoadCancelledError(source)

      const ignoredAnimations = gltf.animations.map(
        (clip, index) => clip.name || `animation-${index}`
      )
      const result = createMeshLoadResult(
        this.format,
        normalized,
        {
          ignoredAnimations,
          warnings:
            ignoredAnimations.length > 0
              ? [`忽略 ${ignoredAnimations.length} 个动画 Clip，使用加载时静态姿态`]
              : [],
        },
        getLoggerManager().now() - startedAt
      )
      const ktx2 = parser.getKtx2Report?.()
      if (ktx2) Object.assign(result.report, { ktx2 })
      reportProgress(1)
      log.debug(
        `event=mesh.gltf.standard.complete source=${source} format=${this.format} elapsedMs=${result.report.elapsedMs.toFixed(
          1
        )} sourceReadMs=${sourceReadMs.toFixed(1)} parseMs=${parseMs.toFixed(
          1
        )} normalizeMs=${normalizeMs.toFixed(1)} primitives=${result.report.stats.primitiveCount}`
      )
      return extensionResults ? attachGltfExtensionResults(result, extensionResults) : result
    } catch (error) {
      parsingFailed = true
      disposeGltfExtensionResults(extensionResults)
      finishRuntimeMonitoring('failed')
      progressive?.close()
      try {
        resources.dispose()
      } catch {
        // 保留原始加载错误；资源跟踪器已经继续尝试清理全部资源。
      }
      parser?.disposeImages?.()
      if (
        error instanceof MeshLoadCancelledError ||
        error instanceof GltfMemoryBudgetExceededError ||
        error instanceof SkinnedMeshNotSupportedError
      ) {
        throw error
      }
      if (error instanceof DracoBudgetExceededError)
        throw new GltfMemoryBudgetExceededError(
          source,
          error.kind === 'geometry' ? 'budget-final-exceeded' : 'budget-transient-exceeded',
          error.actualBytes,
          error.limitBytes
        )
      log.error(
        `${this.format} 加载失败，来源: ${source}，原因: ${
          error instanceof Error ? error.message : String(error)
        }`
      )
      throw new MeshAssetLoadError(this.format, source, error)
    }
  }
}

/** glTF 静态网格 Loader，默认 standard，可按配置采用低内存直接路径。 */
export class GltfMeshLoader implements AssetLoader<MeshLoadRequest, MeshLoadResult> {
  readonly format: 'gltf' | 'glb'
  readonly detectPriority = 10

  private readonly loader: LowMemoryGltfLoader
  private readonly _options: GltfMeshLoaderOptions

  constructor(options: GltfMeshLoaderOptions = {}, format: 'gltf' | 'glb' = 'gltf') {
    this.format = format
    this._options = options
    const standard = new StandardGltfMeshLoader(options, format)
    this.loader = new LowMemoryGltfLoader(standard, format, {
      loading: options.loading,
      optimization: options.optimization,
      dependencies: options.lowMemory,
      extensionRegistry: options.extensionRegistry,
    })
  }

  canLoad(request: MeshLoadRequest): boolean {
    return this.loader.canLoad(request)
  }

  load(request: MeshLoadRequest, context: LoadContext): Promise<MeshLoadResult> {
    const internal = context as MeshLoadContext
    return this.loader.load(request, {
      ...internal,
      loadingManager: this._options.loadingManager ?? internal.loadingManager,
      ktx2Service: this._options.ktx2
        ? borrowMeshKtx2Loader(this._options.ktx2.loader, this._options.ktx2.maxTextureSize)
        : internal.ktx2Service,
      dracoService: this._options.draco ?? internal.dracoService,
    })
  }
}

/** GLB 格式适配器，共用 GltfMeshLoader 解析内核。 */
export class GlbMeshLoader extends GltfMeshLoader {
  constructor(options: GltfMeshLoaderOptions = {}) {
    super(options, 'glb')
  }
}
